import { Action, ActionCreator, Reducer } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { getMfeLogPayload } from '@common/utils/getMfeLogPayload';
import { ClientComs } from '@src/classes/ClientComs';
import { config as sysConfig } from '@src/config';
import type { TefComs } from '@src/types/typings';
import { assert } from '@src/util/helpers';
import { loadScript } from '@src/util/loadScript';
import { checkScenario } from '@src/util/scenario';
import type { RootState } from './index';

declare const window: {
  one?: { // tef-one
    ready?: unknown;
  };
} & Window;

// action types

export const INIT = 'config/INIT';
export const OCCL = 'config/OCCL';
export const ERROR = 'config/ERROR';

// initial state

export interface ConfigState extends TefComs.ConfigOptions {
  // @ts-expect-error TODO: this is a workaround for the fact that the config is not typed correctly
  [key: string]: string | boolean | string[];
  error: boolean;
  occlThemeLoaded: boolean;
  occlVendorsLoaded: boolean;
  occlBundleLoaded: boolean;
  occlReady: boolean;
}

// action creators

/**
 * Logs the current call properties and integration parameters to the logfiles
 */
type Complex = ActionCreator<ThunkAction<void, RootState, void, Action>>;

export const logMicrofrontendParams: Complex = () => function logMicrofrontendParamsInner(_, getState): void {
  try {
    const { config, auth } = getState();
    const client = new ClientComs(config, auth);
    client.logMicrofrontendParams(getMfeLogPayload('tef-coms', config as Record<string, string>)).catch(() => {});
  } catch (error) {
    // Only throw this error in DEBUG mode
    if (localStorage.getItem('tef-coms-debug-mode')) {
      throw error;
    }
  }
};

export const init: ActionCreator<ThunkAction<Promise<void>, RootState, void, Action>> = (
  config: ConfigState,
) => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
) => {
  const { apiUri: apiUriRaw, occluri: occluriRaw } = config;
  const normalize = (uri: string) => (uri ? uri.replace(/\/$/, '') : undefined);
  const apiUri = normalize(apiUriRaw);
  const occluri = normalize(occluriRaw);

  dispatch({
    type: INIT,
    ...config,
    apiUri,
    occluri,
  });

  try {
    const {
      spId, lcId, registerId,
      customerToken, accessToken, registerToken, accesstoken4p,
      lifecycle, mode, apiUri4p, group,
    } = config;
    const { isRaittNoTokensWithInterception } = checkScenario(config);

    assert(lifecycle, 'lifecycle is required');
    assert(apiUri, 'apiuri is required');
    assert(
      !(registerToken && customerToken),
      'registertoken and customertoken are mutually exclusive',
    );
    assert(
      !isRaittNoTokensWithInterception || spId,
      'if no tokens are provided (RAITT), spId is required',
    );
    // When "group" is set it is allowed to have empty lcId (WPCSVM-1485)
    assert(
      !isRaittNoTokensWithInterception || (!lcId && group) || lcId || registerId,
      'if no tokens are provided (RAITT), lcId or registerId is required',
    );
    assert(
      isRaittNoTokensWithInterception || !(customerToken && !accessToken),
      'customertoken requires accesstoken',
    );
    assert(
      !(accesstoken4p && !apiUri4p),
      'accesstoken4p requires apiuri4p',
    );
    assert(
      !(accesstoken4p && !accessToken),
      'accesstoken4p requires accesstoken',
    );
    assert(
      occluri || window.one?.ready,
      'occluri is required unless tef-one is present',
    );
    assert(
      // eslint-disable-next-line sonarjs/regex-complexity
      /^((multiedit(:.+)?(,buttons:.*)?)|(buttons(:(save|saveActive|cancel))?)|(merge|(cattr:.*?)))?$/.test(mode!),
      'mode is invalid',
    );
  } catch (error) {
    dispatch({ type: ERROR, error });
    throw error;
  }

  if (occluri) {
    // load occl libraries
    const {
      occl: { vendors, bundle },
    } = sysConfig;
    const onLoadVendors = () => dispatch({ type: OCCL, occlVendorsLoaded: true });
    const onLoadBundle = () => dispatch({ type: OCCL, occlBundleLoaded: true });
    const onError = () => dispatch({ type: ERROR, error: new Error('loading OCCL failed') });
    const onErrorDummy = () => {};
    vendors(occluri).forEach((lib) => {
      const errorHandler = lib.ignoreNotFound ? onErrorDummy : onError;
      loadScript({ ...lib, onLoad: onLoadVendors, onError: errorHandler });
    });

    bundle(occluri).forEach((lib) => {
      loadScript({ ...lib, onLoad: onLoadBundle, onError });
    });
  }
};

export const occlThemeLoad: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
): Promise<Action> => dispatch({ type: OCCL, occlThemeLoaded: true });

export const occlThemeError: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
): Promise<Action> => dispatch({ type: ERROR, error: new Error('loading OCCL failed') });

type ConfigAction = Action<typeof ERROR | typeof INIT | typeof OCCL> & ConfigState;

const initial: Omit<ConfigState, 'newcustomer' | 'lifecycle' | 'apiUri' | 'occluri'> = {
  error: false,
  occlBundleLoaded: false,
  occlReady: false,
  occlThemeLoaded: false,
  occlVendorsLoaded: false,
};

// reducer

export const reducer: Reducer<ConfigState, ConfigAction> = (
  state = initial as ConfigState,
  action = {} as ConfigAction,
) => {
  const {
    accessToken,
    accesstoken4p,
    apiUri,
    apiUri4p,
    channel,
    comment,
    customerToken,
    error,
    group,
    lcId,
    lifecycle,
    mode,
    newcustomer,
    occlBundleLoaded,
    occlThemeLoaded,
    occlVendorsLoaded,
    occluri,
    references,
    registerId,
    registerToken,
    sessionkey,
    spId,
    stylecontext,
    traceid,
    type,
    usertype,
    vonr,
  } = action;
  switch (type) {
    case ERROR:
      return {
        ...state,
        error,
      };
    case INIT:
      return {
        ...state,
        accessToken,
        accesstoken4p,
        apiUri,
        apiUri4p,
        channel,
        comment,
        customerToken,
        group,
        lcId,
        lifecycle,
        mode,
        newcustomer,
        occluri,
        references,
        registerId,
        registerToken,
        sessionkey,
        spId,
        stylecontext,
        traceid,
        usertype,
        vonr,
      };
    case OCCL: {
      const newOcclThemeLoaded = !!(state.occlThemeLoaded || occlThemeLoaded);
      const newOcclVendorsLoaded = !!(
        state.occlVendorsLoaded || occlVendorsLoaded
      );
      const newOcclBundleLoaded = !!(
        state.occlBundleLoaded || occlBundleLoaded
      );
      const newOcclReady = newOcclThemeLoaded && newOcclVendorsLoaded && newOcclBundleLoaded;
      return {
        ...state,
        occlThemeLoaded: newOcclThemeLoaded,
        occlVendorsLoaded: newOcclVendorsLoaded,
        occlBundleLoaded: newOcclBundleLoaded,
        occlReady: newOcclReady,
      };
    }
    default:
      return state;
  }
};
